/**
 * Canadian Forest Fire Behaviour Prediction (FBP) System fuel types.
 *
 * The codes are the standard cffdrs / Prometheus numbering (1..18) plus the
 * seasonal pairs provincial fuel layers carry (21..24), which the simulator
 * resolves to the leafless or green member from the run date.  They map onto
 * our ``model_subclass`` column and are only meaningful when the polygon's
 * ``model_class`` is ``30`` (Canadian FBP) — exactly as the Scott & Burgan
 * numbers do under class 20.
 *
 * Keep this list in sync with ``fire_spread_new/models/fbp_tables.py`` and
 * ``mitigate_be/api/shared/fbp_codes.py``.
 */

export interface FbpFuelType {
  /** Numeric id stored in ``model_subclass``. */
  code: number;
  /** FBP label, e.g. ``C-2``. */
  name: string;
  /** Family label for grouping in dropdowns. */
  family: "Conifer" | "Deciduous" | "Mixedwood" | "Grass" | "Slash";
  /** Short human description. */
  description: string;
}

export const FBP_FUEL_TYPES: FbpFuelType[] = [
  { code: 1, name: "C-1", family: "Conifer", description: "Spruce-lichen woodland" },
  { code: 2, name: "C-2", family: "Conifer", description: "Boreal spruce" },
  { code: 3, name: "C-3", family: "Conifer", description: "Mature jack or lodgepole pine" },
  { code: 4, name: "C-4", family: "Conifer", description: "Immature jack or lodgepole pine" },
  { code: 5, name: "C-5", family: "Conifer", description: "Red and white pine" },
  { code: 6, name: "C-6", family: "Conifer", description: "Conifer plantation" },
  { code: 7, name: "C-7", family: "Conifer", description: "Ponderosa pine / Douglas-fir" },
  { code: 8, name: "D-1", family: "Deciduous", description: "Leafless aspen" },
  { code: 9, name: "D-2", family: "Deciduous", description: "Green aspen" },
  { code: 22, name: "D-1/2", family: "Deciduous", description: "Aspen, leafless or green by season" },
  { code: 10, name: "M-1", family: "Mixedwood", description: "Boreal mixedwood, leafless" },
  { code: 11, name: "M-2", family: "Mixedwood", description: "Boreal mixedwood, green" },
  { code: 21, name: "M-1/2", family: "Mixedwood", description: "Boreal mixedwood, leafless or green by season" },
  { code: 12, name: "M-3", family: "Mixedwood", description: "Dead balsam fir mixedwood, leafless" },
  { code: 13, name: "M-4", family: "Mixedwood", description: "Dead balsam fir mixedwood, green" },
  { code: 24, name: "M-3/4", family: "Mixedwood", description: "Dead balsam fir mixedwood, leafless or green by season" },
  { code: 14, name: "O-1a", family: "Grass", description: "Matted grass" },
  { code: 15, name: "O-1b", family: "Grass", description: "Standing grass" },
  { code: 23, name: "O-1a/b", family: "Grass", description: "Grass, matted or standing by season" },
  { code: 16, name: "S-1", family: "Slash", description: "Jack or lodgepole pine slash" },
  { code: 17, name: "S-2", family: "Slash", description: "White spruce / balsam slash" },
  { code: 18, name: "S-3", family: "Slash", description: "Coastal cedar / hemlock / Douglas-fir slash" },
];

export const FBP_MODEL_CLASS_NAME = "Canadian FBP";

export const isFbpModelClass = (name?: string | null): boolean =>
  name === FBP_MODEL_CLASS_NAME;

const _BY_CODE: Record<number, FbpFuelType> = FBP_FUEL_TYPES.reduce(
  (acc, ft) => ({ ...acc, [ft.code]: ft }),
  {},
);

export const getFbpFuelType = (code: number | null | undefined): FbpFuelType | null => {
  if (code == null) return null;
  return _BY_CODE[code] ?? null;
};

/** Short label for display, e.g. ``C-2 · Boreal spruce``. */
export const formatFbpLabel = (ft: FbpFuelType): string => `${ft.name} · ${ft.description}`;
